import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router";
import Topbar from "../../../../../component/TopBar";
import { useNotify } from "../../../../../component/NotificationProvider";
import { useSelfEmployee } from "../../../../../services/useOrgData";
import { getSelfLeaveBalance } from "../../../../../services/leave";
import { getMyReviews, getMyGoals } from "../../../../../services/performance";
import { getSelfAttendanceRequests } from "../../../../../services/attendanceRequest";
import { getSelfPayslips } from "../../../../../services/payroll";
import { getAllHolidays } from "../../../../../services/holiday";
import { getMyChecklist } from "../../../../../services/onboarding";
import useTodayAttendance from "../../../../../services/useTodayAttendance";
import { tokens, displayFont, radius } from "../../../../../theme";

import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { pressable } from "../../../../../component/pressableProps";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip, PersonCell } from "../../../../../component/listing";
import { getEmployeeExpense } from "../../../../../services/expense";
import { getLeaveCalendar } from "../../../../../services/leave";
import {
  payOutlook, leaveOutlook, elapsedSince, recentClaims, awayThisWeek,
} from "./selfDashboard";
/**
 * The employee's front door.
 *
 * Employees had six nav items and no landing screen: signing in dropped you on
 * whichever list happened to be first. This answers the two questions someone
 * actually opens the app with — what do I owe anyone, and where do I stand —
 * and gets out of the way.
 *
 * Everything here is self-scoped and already existed as an endpoint. Nothing is
 * computed from a number the page invented: where the data does not support a
 * figure, the card says so rather than showing a zero that reads as fact.
 */

const cardSx = {
  background: tokens.card,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${radius.card}px`,
  padding: "1.25rem",
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const timeOf = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "--";

// The day record's departure is minutes since midnight in the employee's
// zone, not a timestamp — read as one it came out as an early-morning time.
const minutesLabel = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n)) return "";
  const h = Math.floor(n / 60) % 24, min = n % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(min).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

const dayLabel = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

// "in 3 days" is what someone wants from a holiday list, not a date to subtract
const daysUntil = (d) => {
  const start = new Date(new Date().toDateString());
  const then = new Date(new Date(d).toDateString());
  return Math.round((then - start) / 86400000);
};

const untilLabel = (d) => {
  const n = daysUntil(d);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  return `in ${n} days`;
};


const QuickAction = ({ icon, label, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    onClick={onClick}
    sx={{
      ...cardSx,
      flex: 1,
      minWidth: 0,
      cursor: "pointer",
      transition: "border-color 120ms cubic-bezier(0.2, 0, 0.2, 1), background 120ms cubic-bezier(0.2, 0, 0.2, 1)",
      "&:hover": { borderColor: "#DCC5B3", background: tokens.surfaceSubtle },
    }}
  >
    <Box
      sx={{
        width: "2.25rem",
        height: "2.25rem",
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `${tokens.accentTint}`,
        color: tokens.accent,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{label}</Box>
  </Stack>
);

// A row in "Needs you". Every one of these is something the employee is
// holding up — not a notification, an obligation.
const TodoRow = ({ label, detail, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    onClick={onClick}
    sx={{
      padding: "1rem 0",
      borderBottom: `1px solid ${tokens.border}`,
      cursor: "pointer",
      "&:last-of-type": { borderBottom: "none" },
      "&:hover .todo-label": { color: tokens.accent },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Box className="todo-label" sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>
        {label}
      </Box>
      {detail && (
        <Box sx={{ mt: "0.25rem", fontSize: "0.8125rem", color: tokens.textMuted }}>{detail}</Box>
      )}
    </Box>
    <ChevronRightIcon sx={{ color: tokens.textMuted, flexShrink: 0 }} />
  </Stack>
);

const SectionTitle = ({ children }) => (
  <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.75rem" }}>{children}</Box>
);

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { data: me } = useSelfEmployee();

  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState([]);
  const [claims, setClaims] = useState([]);
  // Clocking out closes the day; it gets a confirmation.
  const [clockOutOpen, setClockOutOpen] = useState(false);
  const [calendar, setCalendar] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [checklist, setChecklist] = useState(null);

  // Shared with the admin dashboard and kept in step with the phone.
  const { inTime, outTime, isCheckedIn, punching: checkLoading, punch } = useTodayAttendance();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      // Each is allowed to fail on its own: one dead endpoint should cost its
      // own card, not the whole page.
      const [bal, rev, gl, req, pay, hol, chk, exp, cal] = await Promise.all([
        getSelfLeaveBalance().catch(() => null),
        getMyReviews().catch(() => null),
        getMyGoals().catch(() => null),
        getSelfAttendanceRequests().catch(() => null),
        getSelfPayslips().catch(() => null),
        getAllHolidays().catch(() => null),
        getMyChecklist().catch(() => null),
        getEmployeeExpense().catch(() => null),
        // Who else is off, for the "your team this week" card. A failure here
        // costs one card, not the screen.
        getLeaveCalendar({ status: "approved" }).catch(() => null),
      ]);
      if (!active) return;
      setBalances(Array.isArray(bal?.data) ? bal.data : []);
      setReviews(Array.isArray(rev?.data) ? rev.data : []);
      setGoals(Array.isArray(gl?.data) ? gl.data : []);
      setRequests(Array.isArray(req?.data) ? req.data : []);
      setPayslips(Array.isArray(pay?.data) ? pay.data : []);
      setHolidays(Array.isArray(hol?.data) ? hol.data : []);
      setChecklist(chk?.data || null);
      setClaims(Array.isArray(exp?.data) ? exp.data : []);
      setCalendar(Array.isArray(cal?.data) ? cal.data : []);
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const handleCheck = async () => {
    try {
      const done = await punch();
      notify.success(done === "IN" ? "Checked in" : "Checked out");
    } catch (err) {
      // the card has already re-read the server; say what it said
      notify.error(err?.message || "Couldn't update attendance");
    }
  };


  const pendingRequests = useMemo(
    () => requests.filter((r) => r?.status === "Pending" || r?.status === "pending"),
    [requests]
  );

  // Weighted the way the review does it, so the number here and the number on
  // the goals page cannot disagree.


  const nextHolidays = useMemo(
    () => holidays
      .filter((h) => h?.startDate && daysUntil(h.startDate) >= 0)
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      .slice(0, 3),
    [holidays]
  );

  // What the employee is holding up. Ordered by who is waiting on them.
  const todos = useMemo(() => {
    const items = [];
    // An unfinished profile, named by what is actually missing rather than as
    // a blanket "complete your profile" — somebody who has done four of five
    // sections should be told which one is left.
    if (me?.profile && !me.profile.complete) {
      items.push({
        label: `Finish your profile — ${me.profile.missingSections.join(", ")} still needed`,
        detail: me.profile.payrollReady
          ? `${me.profile.sectionsComplete} of ${me.profile.sectionsTotal} sections done`
          : "Payroll cannot be set up without your bank and PAN details",
        to: "/app/complete-profile",
      });
    }
    // Onboarding first: for a new joiner nothing else on this page matters
    // until their documents are in, and these have real due dates.
    (checklist?.items || [])
      .filter((i) => i.status === "pending" && i.owner === "employee")
      .sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0))
      .slice(0, 3)
      .forEach((i) => {
        const overdue = i.dueDate && new Date(i.dueDate) < new Date();
        items.push({
          label: i.label,
          detail: i.dueDate
            ? `${overdue ? "Overdue — was due" : "Due"} ${new Date(i.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
            : "Part of your onboarding",
          to: "/app/my-onboarding",
        });
      });
    // "pending" is the first state in the Review enum: nobody has written
    // anything yet, and the employee's self-assessment is what unblocks it.
    reviews
      .filter((r) => r?.status === "pending")
      .forEach((r) =>
        items.push({
          label: "Your self-review is due",
          detail: "Your manager cannot start theirs until you submit",
          to: `/app/performance/review/${r._id}`,
        })
      );
    goals
      .filter((g) => (Number(g.progress) || 0) === 0 && g?.status !== "cancelled")
      .slice(0, 2)
      .forEach((g) =>
        items.push({
          label: `Goal not started — ${g.objective}`,
          detail: "Update progress so your review reflects it",
          to: "/app/my-goals",
        })
      );
    pendingRequests.forEach((r) =>
      items.push({
        label: `${r.type === "SHIFT_CHANGE" ? "Shift change" : "Attendance"} request awaiting approval`,
        detail: r.reason || "Waiting on your approver",
        to: "/app/employee-attendance-dashboard",
      })
    );
    return items;
  }, [reviews, goals, pendingRequests, checklist, me]);

  const name = me?.fname ? `${greeting()}, ${me.fname}` : greeting();
  const pay = useMemo(() => payOutlook(payslips), [payslips]);
  const leave = useMemo(() => leaveOutlook(balances), [balances]);
  const myClaims = useMemo(() => recentClaims(claims), [claims]);
  const away = useMemo(() => awayThisWeek(calendar, 7, new Date(), me?._id), [calendar, me]);
  const clockedFor = elapsedSince(inTime);

  return (
    <Box sx={{ width: "100%" }}>
      <Topbar MainHeading={name} />

      <Box sx={{ padding: "0 1.5rem 2rem" }}>
        {/*
          Three cards, not four counters. The book is explicit that this screen
          carries "no approvals block, no company figures, no headcount" — an
          employee's whole relationship with PayLoom is their own pay, their own
          time and their own paperwork.
        */}
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} sx={{ mb: "1.25rem" }} alignItems="stretch">
          {/* Their pay, in the deep plum. The one panel on this screen allowed
              colour, and it is theirs rather than the company's. */}
          <Box
            sx={{
              flex: 1.1,
              minWidth: 0,
              background: tokens.sidebarBg,
              color: tokens.card,
              borderRadius: "12px",
              p: "1.625rem",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.75, mb: "0.875rem" }}>
              {pay?.paid ? "Last paid" : "Your pay"}
            </Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "2.125rem", fontWeight: 800, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", lineHeight: 1.05 }}>
              {pay ? `₹${Math.round(pay.estimate).toLocaleString("en-IN")}` : "—"}
            </Box>
            <Box sx={{ fontSize: "0.8125rem", opacity: 0.8, mt: "0.375rem", mb: "1.375rem" }}>
              {pay
                ? [`Net for ${pay.basedOn}`, pay.accountEnding ? `to account ending ${pay.accountEnding}` : null]
                    .filter(Boolean).join(", ")
                : "No payslip published yet"}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1.25}>
              {pay && (
                <Box
                  {...pressable(() => navigate(`/app/employee-payslips/${pay.latestId}`))}
                  sx={{ background: tokens.card, color: tokens.sidebarBg, borderRadius: "8px", px: "1.125rem", py: "0.625rem", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}
                >
                  {pay.basedOn.split(" ")[0]} payslip
                </Box>
              )}
              <Box
                {...pressable(() => navigate("/app/employee-payslips"))}
                sx={{ border: "1px solid rgba(255,255,255,0.32)", color: tokens.card, borderRadius: "8px", px: "1.125rem", py: "0.625rem", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}
              >
                All payslips
              </Box>
            </Stack>
          </Box>

          {/* Their leave. */}
          <Box sx={{ ...cardSx, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <SectionTitle>Your leave</SectionTitle>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: "0.875rem" }}>
              <Box sx={{ fontFamily: displayFont, fontSize: "2.125rem", fontWeight: 800, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: tokens.text }}>
                {leave ? leave.left : "—"}
              </Box>
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
                {leave?.total != null ? `days left of ${leave.total}` : "days left"}
              </Box>
            </Stack>
            {/* Only drawn when an entitlement is known — a bar against an
                unknown total would be a made-up proportion. */}
            {leave?.share != null && (
              <Box sx={{ height: "0.5rem", borderRadius: "4px", background: tokens.border, overflow: "hidden", mb: "1rem" }}>
                <Box sx={{ width: `${leave.share * 100}%`, height: "100%", background: tokens.primary }} />
              </Box>
            )}
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6, mb: "1.25rem" }}>
              {leave?.types?.length
                ? leave.types.map((t) => `${t.name}: ${t.left}`).join(" · ")
                : "No leave types assigned yet."}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box
              {...pressable(() => navigate("/app/leave-request"))}
              sx={{ background: tokens.primary, color: tokens.onPrimary, borderRadius: "8px", px: "1.125rem", py: "0.625rem", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer", display: "inline-block", alignSelf: "flex-start" }}
            >
              Request leave
            </Box>
          </Box>

          {/* Their time. */}
          <Box sx={{ ...cardSx, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <SectionTitle>Today</SectionTitle>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: "0.5rem" }}>
              <Box
                sx={{
                  width: "0.5rem", height: "0.5rem", borderRadius: "50%", flex: "none",
                  background: isCheckedIn ? tokens.successText : tokens.textPlaceholder,
                  boxShadow: isCheckedIn ? `0 0 0 4px ${tokens.successTint}` : "none",
                }}
              />
              <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>
                {inTime ? `Clocked in ${timeOf(inTime)}` : "Not clocked in yet"}
              </Box>
            </Stack>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6 }}>
              {outTime
                ? `Clocked out ${minutesLabel(outTime)}.`
                : clockedFor
                ? `${clockedFor} so far today.`
                : "Your day has not started."}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box
              component="button"
              type="button"
              disabled={checkLoading}
              /*
                Clocking in is reversible in the sense that the day is still
                running; clocking out ends it, and the recorded hours are what
                payroll and the approver see. So the out direction confirms and
                the in direction does not.
              */
              onClick={() => {
                if (checkLoading) return;
                if (isCheckedIn) setClockOutOpen(true);
                else handleCheck();
              }}
              sx={{
                mt: "1.25rem", padding: "0.625rem 1.125rem", borderRadius: "8px",
                fontFamily: "inherit", fontSize: "0.8125rem", fontWeight: 600,
                cursor: checkLoading ? "default" : "pointer", opacity: checkLoading ? 0.6 : 1,
                color: isCheckedIn ? tokens.textMuted : tokens.onPrimary,
                background: isCheckedIn ? "transparent" : tokens.primary,
                border: `1px solid ${isCheckedIn ? tokens.borderStrong : "transparent"}`,
                alignSelf: "flex-start",
              }}
            >
              {checkLoading ? "Working…" : isCheckedIn ? "Clock out" : "Clock in"}
            </Box>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
          <Box sx={{ ...cardSx, flex: 2, minWidth: 0 }}>
            <SectionTitle>Needs you</SectionTitle>
            {loading ? (
              <CircularProgress size={22} sx={{ color: tokens.textMuted }} />
            ) : todos.length === 0 ? (
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, py: "0.5rem" }}>
                Nothing waiting on you.
              </Box>
            ) : (
              todos.map((t, i) => (
                <TodoRow key={i} label={t.label} detail={t.detail} onClick={() => navigate(t.to)} />
              ))
            )}
          </Box>

          <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
            <SectionTitle>Upcoming holidays</SectionTitle>
            {loading ? (
              <CircularProgress size={22} sx={{ color: tokens.textMuted }} />
            ) : nextHolidays.length === 0 ? (
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, py: "0.5rem" }}>
                None scheduled.
              </Box>
            ) : (
              nextHolidays.map((h) => (
                <Stack
                  key={h._id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    py: "0.75rem",
                    borderBottom: `1px solid ${tokens.border}`,
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{h.name}</Box>
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{dayLabel(h.startDate)}</Box>
                  </Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, flexShrink: 0 }}>
                    {untilLabel(h.startDate)}
                  </Box>
                </Stack>
              ))
            )}
          </Box>
        </Stack>

        {/* Their claims and their colleagues — the last two of the book's
            cards. Neither is a queue: an employee wants to know whether the
            dinner they paid for has been dealt with, and who is off. */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} sx={{ mb: "1.25rem" }} alignItems="stretch">
          <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
            <SectionTitle>Your claims</SectionTitle>
            {myClaims.length ? (
              <Box>
                {myClaims.map((c) => (
                  <Stack
                    key={c._id}
                    direction="row"
                    alignItems="center"
                    spacing={1.75}
                    sx={{ py: "0.6875rem", borderBottom: `1px solid ${tokens.border}`, fontSize: "0.875rem" }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ color: tokens.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.label}
                      </Box>
                      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>
                        {c.reimbursed ? "Reimbursed" : `Submitted ${c.submitted || "—"}`}
                      </Box>
                    </Box>
                    <Box sx={{ fontVariantNumeric: "tabular-nums", color: tokens.text, whiteSpace: "nowrap" }}>
                      ₹{Math.round(c.amount).toLocaleString("en-IN")}
                    </Box>
                    <StatusChip
                      label={c.reimbursed ? "Reimbursed" : c.status}
                      tone={c.reimbursed ? "success" : c.status === "pending" ? "muted" : c.status === "approved" ? "success" : "danger"}
                    />
                  </Stack>
                ))}
              </Box>
            ) : (
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, py: "0.5rem" }}>
                You have not submitted any claims.
              </Box>
            )}
            <Box sx={{ pt: "0.875rem" }}>
              <Box
                {...pressable(() => navigate("/app/my-expenses"))}
                sx={{ display: "inline-block", border: `1px solid ${tokens.borderStrong}`, color: tokens.textMuted, borderRadius: "8px", px: "1rem", py: "0.5625rem", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}
              >
                Submit a claim
              </Box>
            </Box>
          </Box>

          <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
            <SectionTitle>Your team this week</SectionTitle>
            {away.length ? (
              <Box>
                {away.map((a2) => (
                  <Stack key={a2._id} direction="row" alignItems="center" spacing={1.5} sx={{ py: "0.6875rem", borderBottom: `1px solid ${tokens.border}` }}>
                    <PersonCell name={a2.name} secondary={a2.until ? `Away until ${a2.until}` : "Away"} size="1.75rem" />
                  </Stack>
                ))}
              </Box>
            ) : (
              /* An absence of news is worth stating on a card about other
                 people: a blank one reads as not loaded. */
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, py: "0.5rem", lineHeight: 1.6 }}>
                Nobody else is away in the next seven days.
              </Box>
            )}
          </Box>
        </Stack>

        <SectionTitle>Quick actions</SectionTitle>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <QuickAction
            icon={<EventBusyOutlinedIcon />}
            label="Apply for leave"
            onClick={() => navigate("/app/leave-request")}
          />
          <QuickAction
            icon={<ReceiptLongOutlinedIcon />}
            label="Claim an expense"
            onClick={() => navigate("/app/employee-expense/add-new")}
          />
          <QuickAction
            icon={<FlagOutlinedIcon />}
            label="Update my goals"
            onClick={() => navigate("/app/my-goals")}
          />
          <QuickAction
            icon={<PaymentsOutlinedIcon />}
            label="My payslips"
            onClick={() => navigate("/app/employee-payslips")}
          />
        </Stack>
      </Box>

      <Modal open={clockOutOpen} onClose={() => setClockOutOpen(false)} width="26rem">
        <ModalHead
          title="Clock out for today?"
          subtitle={
            inTime && clockedFor
              ? `You clocked in at ${timeOf(inTime)}, so today records ${clockedFor}.`
              : "This closes your day."
          }
          onClose={() => setClockOutOpen(false)}
        />
        <ModalBody>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, lineHeight: 1.6 }}>
            Your approver sees the recorded hours. If you clock out early by mistake,
            ask them to correct the day rather than clocking in again.
          </Box>
        </ModalBody>
        <ModalFoot note="">
          <BtnSecondary onClick={() => setClockOutOpen(false)} sx={{ borderRadius: "8px" }}>
            Not yet
          </BtnSecondary>
          <BtnPrimary
            onClick={() => { setClockOutOpen(false); handleCheck(); }}
            disabled={checkLoading}
            sx={{ borderRadius: "8px" }}
          >
            Clock out
          </BtnPrimary>
        </ModalFoot>
      </Modal>
    </Box>
  );
};

export default EmployeeDashboard;
